import { useEffect, useRef, useState } from 'react';
import { useScrollRestoration } from '@/lib/scrollRestoration';
import { format } from 'date-fns';
import {
  AlertTriangle,
  ExternalLink,
  Package as PackageIcon,
  PackageCheck,
  Plus,
  RefreshCw,
  Truck,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getPackages, isResolvedProblem, refreshPackages, registerPackage, type Package } from '@/api/packages';
import { Can } from '@/auth/guards';
import { statusInk } from '@/lib/status';
import { pkgStatusLabel } from '@/features/orders/lib';

// Carrier strings EasyPost accepts on this account (server also maps common
// aliases). Empty value = let EasyPost auto-detect.
const CARRIER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'UPS', label: 'UPS' },
  { value: 'USPS', label: 'USPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHLExpress', label: 'DHL Express' },
  { value: 'Estes', label: 'Estes (freight)' },
  { value: 'RLCarriers', label: 'R+L Carriers (freight)' },
  { value: 'TForce', label: 'TForce (freight)' },
  { value: 'ABF', label: 'ABF (freight)' },
];

function statusIcon(status: string) {
  const iconClass = 'h-4 w-4 shrink-0';
  switch (status) {
    case 'delivered':
      return <PackageCheck className={cn(iconClass, statusInk('success'))} />;
    case 'out_for_delivery':
    case 'available_for_pickup':
    case 'in_transit':
      return <Truck className={cn(iconClass, statusInk('info'))} />;
    case 'failure':
    case 'return_to_sender':
    case 'error':
      return <AlertTriangle className={cn(iconClass, statusInk('danger'))} />;
    default:
      return <PackageIcon className={cn(iconClass, 'text-muted-foreground')} />;
  }
}

// "FedExDefault" is an EasyPost account-name artifact, not a carrier name.
function displayCarrier(carrier: string | null): string | null {
  return carrier ? carrier.replace(/Default$/, '') : carrier;
}

function formatDay(dateString: string | null): string | null {
  if (!dateString) return null;
  try {
    // Date-only carrier ETAs are calendar dates, not UTC instants.
    const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateString) ? `${dateString}T12:00:00` : dateString);
    return format(parsed, 'EEE MMM d');
  } catch {
    return null;
  }
}

function etaLine(pkg: Package): string {
  if (pkg.status === 'delivered') {
    const day = formatDay(pkg.deliveredAt);
    return `Delivered${day ? ` ${day}` : ''}${pkg.signedBy ? ` · signed ${pkg.signedBy}` : ''}`;
  }
  if (isResolvedProblem(pkg)) {
    const day = formatDay(pkg.problemResolvedAt);
    return `Resolved${day ? ` ${day}` : ''}`;
  }
  const day = formatDay(pkg.estDeliveryDate);
  return day ? `ETA ${day}` : pkgStatusLabel(pkg.status);
}

function latestScanLine(pkg: Package): string | null {
  const scan = pkg.trackingDetails.length
    ? pkg.trackingDetails[pkg.trackingDetails.length - 1]
    : null;
  if (!scan) return null;
  const location = scan.tracking_location?.city
    ? ` — ${scan.tracking_location.city}${scan.tracking_location.state ? `, ${scan.tracking_location.state}` : ''}`
    : '';
  return `${scan.message || scan.status}${location}`;
}

interface AddPackageFormProps {
  onAdded: (warning: string | null) => void;
  onCancel: () => void;
}

function AddPackageForm({ onAdded, onCancel }: AddPackageFormProps) {
  const [trackingCode, setTrackingCode] = useState('');
  const [carrier, setCarrier] = useState('auto');
  const [vendor, setVendor] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [boxes, setBoxes] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = trackingCode.replace(/\s+/g, '');
    if (code.length < 6) {
      setError('Enter a tracking number');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const boxCount = Number.parseInt(boxes, 10);
      const result = await registerPackage({
        tracking_code: code,
        carrier: carrier !== 'auto' ? carrier : undefined,
        vendor: vendor.trim() || undefined,
        order_ref: orderRef.trim() || undefined,
        package_count: Number.isFinite(boxCount) && boxCount > 1 ? boxCount : undefined,
      });
      onAdded(result.easypost_error);
    } catch (err) {
      console.error('Failed to add package:', err);
      setError(err instanceof Error ? err.message : 'Failed to add package');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-4 py-3 border-b bg-muted/20 flex flex-col gap-2">
      <Input
        value={trackingCode}
        onChange={(e) => setTrackingCode(e.target.value)}
        placeholder="Tracking number"
        className="h-8 text-sm"
        autoFocus
      />
      <div className="grid grid-cols-2 gap-2">
        <Select value={carrier} onValueChange={setCarrier}>
          <SelectTrigger className="h-8 text-sm">
            <SelectValue placeholder="Carrier" />
          </SelectTrigger>
          <SelectContent>
            {CARRIER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={boxes}
          onChange={(e) => setBoxes(e.target.value)}
          type="number"
          min={1}
          max={99}
          placeholder="Boxes"
          className="h-8 text-sm"
          title="Number of boxes"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          placeholder="Vendor (who from)"
          className="h-8 text-sm"
        />
        <Input
          value={orderRef}
          onChange={(e) => setOrderRef(e.target.value)}
          placeholder="Order / invoice #"
          className="h-8 text-sm"
        />
      </div>
      {error && <p className={cn('text-xs', statusInk('danger'))}>{error}</p>}
      <div className="flex gap-2 justify-end">
        <Button type="button" onClick={onCancel} variant="ghost" size="sm" className="h-7 text-xs">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} size="sm" className="h-7 text-xs">
          {isSubmitting ? 'Adding…' : 'Track package'}
        </Button>
      </div>
    </form>
  );
}

export interface PackagesFeedProps {
  className?: string;
  inDialog?: boolean;
  /** Fill the parent instead of capping the list at popover height. */
  fullHeight?: boolean;
}

/**
 * Incoming-packages feed: live EasyPost statuses, manual add, re-check.
 * Rendered inline by the Orders sub-view (Tools → Packages); previously the
 * body of the packages popover.
 */
export function PackagesFeed({ className, inDialog, fullHeight }: PackagesFeedProps) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  useScrollRestoration({
    scopeKey: inDialog ? 'packages:dialog' : 'packages:feed',
    viewportRef: scrollViewportRef,
    ready: !isLoading,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addWarning, setAddWarning] = useState<string | null>(null);
  const requestSeqRef = useRef(0);

  const fetchPackages = async () => {
    const seq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPackages();
      if (seq !== requestSeqRef.current) return;
      setPackages(data);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('Failed to fetch packages:', err);
      setError('Failed to load packages');
    } finally {
      if (seq === requestSeqRef.current) setIsLoading(false);
    }
  };

  // Refresh = poll EasyPost server-side (webhooks make this rarely necessary,
  // but it's the manual "check right now" affordance), then refetch.
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshPackages();
    } catch (err) {
      console.error('Failed to refresh packages:', err);
    } finally {
      setIsRefreshing(false);
    }
    fetchPackages();
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const groups = [
    { id: 'exceptions', label: 'Delivery exceptions', statuses: ['failure', 'return_to_sender', 'error'] },
    { id: 'arriving', label: 'Arriving now', statuses: ['out_for_delivery', 'available_for_pickup'] },
    { id: 'transit', label: 'In transit', statuses: ['in_transit'] },
    { id: 'pending', label: 'Awaiting shipment', statuses: ['unknown', 'pre_transit'] },
    { id: 'delivered', label: 'Recently delivered', statuses: ['delivered'] },
    { id: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
    { id: 'resolved', label: 'Resolved problems', statuses: [] as string[] },
  ];
  const matching = packages.filter(pkg => `${pkg.vendor || ''} ${pkg.carrier || ''} ${pkg.trackingCode} ${pkg.orderRef || ''}`.toLowerCase().includes(query.toLowerCase()));
  // A settled problem keeps its carrier status but is no longer an exception.
  const groupOf = (pkg: Package) => isResolvedProblem(pkg)
    ? 'resolved'
    : groups.find(group => group.statuses.includes(pkg.status))?.id ?? 'pending';
  const grouped = groups.map(group => ({ ...group, rows: matching.filter(pkg => groupOf(pkg) === group.id) })).filter(group => (filter === 'all' || group.id === filter) && group.rows.length);


  const renderRow = (pkg: Package) => {
    const scanLine = latestScanLine(pkg);
    const trackingHref =
      pkg.publicUrl || `https://www.google.com/search?q=${encodeURIComponent(pkg.trackingCode)}`;
    return (
      <a
        key={pkg.id}
        data-scroll-key={`package:${pkg.id}`}
        href={trackingHref}
        target="_blank"
        rel="noopener noreferrer"
        className="block px-5 py-4 hover:bg-accent/50 transition-colors border-b border-border/40 last:border-0 group"
      >
        <div className="flex flex-wrap items-center justify-between mb-1.5 gap-2">
          <span className="flex items-center gap-2 min-w-0">
            {isResolvedProblem(pkg)
              ? <PackageCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
              : statusIcon(pkg.status)}
            <span
              className={cn(
                'text-sm font-medium truncate',
                pkg.status === 'delivered' ? 'text-muted-foreground' : 'text-foreground'
              )}
            >
              {pkg.vendor || displayCarrier(pkg.carrier) || 'Package'}
              {pkg.packageCount > 1 ? ` (${pkg.packageCount})` : ''}
            </span>
          </span>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {etaLine(pkg)}
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground pl-6">
          <span className="truncate">
            {isResolvedProblem(pkg) ? `Resolved: ${pkg.problemResolution}` : scanLine || pkgStatusLabel(pkg.status)}
          </span>
        </div>

        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground pl-6">
          <span className="truncate">
            {pkg.carrier ? `${displayCarrier(pkg.carrier)} · ` : ''}
            {pkg.trackingCode}
            {pkg.orderRef ? ` · ${pkg.orderRef}` : ''}
          </span>
          <ExternalLink className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </div>
      </a>
    );
  };

  return (
    <div className={cn('flex flex-col', fullHeight && 'min-h-0 flex-1', className)}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-2.5 border-b border-border bg-card px-5 py-3.5 max-md:p-3 [&_input]:min-w-0 max-md:[&>input]:w-full max-md:[&>input]:flex-[1_1_100%] justify-between',
          inDialog && 'py-4.5 pr-14'
        )}
      >
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Tracked shipments
        </h3>
        {/* Registering a tracking number and forcing an EasyPost re-poll are
            both POSTs the server gates on orders:write:packages; the feed itself is
            readable with the base read alone. */}
        <Can code="orders:write:packages">
        <div className="flex items-center gap-1">
          <Button
            onClick={() => {
              setShowAddForm((open) => !open);
              setAddWarning(null);
            }}
            variant="ghost"
            size="sm"
            className="h-9 gap-2 px-3"
            aria-label="Track a package manually"
          >
            <span>{showAddForm ? 'Close form' : 'Track package'}</span>
            {showAddForm ? (
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            variant="ghost"
            size="sm"
            className="h-9 gap-2 px-3"
            aria-label="Refresh carrier tracking"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5 text-muted-foreground', isRefreshing && 'animate-spin')}
            />
          </Button>
        </div>
        </Can>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-border bg-card px-5 py-3.5 max-md:p-3 [&_input]:min-w-0 max-md:[&>input]:w-full max-md:[&>input]:flex-[1_1_100%]"><Input value={query} onChange={e => setQuery(e.target.value)} aria-label="Search packages" placeholder="Search vendor, carrier, tracking…" className="max-w-sm" /><Select value={filter} onValueChange={setFilter}><SelectTrigger aria-label="Delivery status" className="w-48"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All delivery states</SelectItem>{groups.map(group => <SelectItem value={group.id} key={group.id}>{group.label}</SelectItem>)}</SelectContent></Select><span className="text-xs text-muted-foreground">{grouped.reduce((sum, group) => sum + group.rows.length, 0)} packages</span></div>
      {showAddForm && (
        <AddPackageForm
          onAdded={(warning) => {
            setShowAddForm(false);
            setAddWarning(warning);
            fetchPackages();
          }}
          onCancel={() => setShowAddForm(false)}
        />
      )}
      {addWarning && (
        <div className="px-4 py-2 border-b bg-warning-surface text-xs text-warning-ink flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            Saved, but tracking couldn't start: {addWarning}. It will retry on the next refresh —
            or re-add with the correct carrier.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="p-8 text-center">
          <RefreshCw className="h-5 w-5 animate-spin mx-auto text-muted-foreground/50" />
        </div>
      ) : error ? (
        <div className="p-4 text-center">
          <p className={cn('text-xs', statusInk('danger'))}>{error}</p>
          <Button onClick={fetchPackages} variant="ghost" size="sm" className="mt-2 h-6 text-xs">
            Retry
          </Button>
        </div>
      ) : packages.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-sm text-muted-foreground">No packages being tracked</p>
        </div>
      ) : (
        <div ref={scrollViewportRef} className={cn('flex-1 overflow-y-auto', !fullHeight && 'max-h-[420px]')}>
          <div className="py-1">
            {grouped.map(group => <section key={group.id}><h3 className="flex w-full items-center gap-2 border-b border-border bg-card px-5 py-3 text-[12px] font-semibold text-muted-foreground">{group.label}<span className="ml-auto tabular-nums">{group.rows.length}</span></h3>{group.rows.map(renderRow)}</section>)}
            {grouped.length === 0 && <p className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground">No packages match these filters.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
